import React, { ReactNode, useMemo } from "react";
import {
  GlobalStyles,
  GlobalStylesProps,
  Theme,
  useTheme,
} from "@mui/material";
import { merge } from "lodash";

const defaultGlobalStyles = (
  theme: Theme,
  options?: { disableUserSelect?: boolean }
): GlobalStylesProps["styles"] => {
  return {
    html: {
      height: "100%",
      width: "100vw",
      margin: 0,
      WebkitOverflowScrolling: "touch",
      touchAction: "manipulation", // Improves touch behavior
      backgroundColor: theme.palette.background.default,
    },
    body: {
      height: "100%",
      width: "100vw",
      margin: 0,
      WebkitOverflowScrolling: "touch",
      // Fix for Mobile Safari height issues
      minHeight: "-webkit-fill-available",
      backgroundColor: theme.palette.background.default,
    },
    "#root": {
      height: "100%", // Changed from "stretch" for better iOS compatibility
      width: "100vw",
      margin: 0,
      display: "flex",
      flexDirection: "column",
      WebkitOverflowScrolling: "touch",
      userSelect: options?.disableUserSelect === true ? "none" : undefined,
      // Ensure content is not hidden behind stuff on mobile
      paddingTop: "env(safe-area-inset-top, 0)",
      paddingLeft: "env(safe-area-inset-left, 0)",
      paddingRight: "env(safe-area-inset-right, 0)",
      paddingBottom: "env(safe-area-inset-bottom, 0)",
      backgroundColor: theme.palette.background.default,
    },
  };
};

export interface AppLayoutProps {
  additionalGlobalStyles?: (theme: Theme) => GlobalStylesProps["styles"];
  disableUserSelect?: boolean;
  children: ReactNode;
}

export const AppLayout = (props: AppLayoutProps) => {
  const theme = useTheme();

  const globalStyles: GlobalStylesProps["styles"] = useMemo(() => {
    return merge(
      defaultGlobalStyles(theme),
      props.additionalGlobalStyles?.(theme)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme]);

  return (
    <>
      <GlobalStyles styles={globalStyles} />
      {props.children}
    </>
  );
};
